<script setup>
/**
 * pages/attendance/kiosk.vue — Addendum 08, Modul AA
 * ─────────────────────────────────────────────────────────
 * Kiosk mode netral: tablet sudah login sebagai siapa saja (mis. dibuka
 * manager pagi hari) — staff yang SESUNGGUHNYA absen diidentifikasi murni
 * dari PIN/QR yang diinput di layar ini, bukan dari sesi login device.
 * Layar ini SENGAJA tidak menampilkan identitas device yang sedang login.
 *
 * Scan QR: pakai <input type="file" capture="environment"> (pola sama
 * dengan purchase/receipt-scan — kamera belakang device langsung terbuka),
 * BUKAN live-video-stream — lebih sederhana & konsisten dengan pola yang
 * sudah ada di project, dan tetap "kamera" sesuai permintaan addendum.
 * Decode QR dari foto lewat BarcodeDetector API bawaan browser (Chrome/
 * Android WebView modern) — TANPA menambah dependency baru. Kalau browser
 * tidak mendukung BarcodeDetector, arahkan ke Input PIN sebagai fallback.
 */
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    action: 'clock',
    subject: 'Attendance',
  },
})

const tenantName = ref('')

const fetchTenantName = async () => {
  try {
    const response = await useApi('/api/v1/settings').get()
    const data = response?.data ?? response

    tenantName.value = data?.tenant?.name ?? ''
  } catch {
    tenantName.value = ''
  }
}

// Patch — foto verifikasi absen (opsional per tenant). GET kiosk-info
// SENGAJA endpoint terpisah dari GET attendance/settings (gerbang
// Attendance.clock, bukan Attendance.read) -- device kiosk bisa login
// sebagai cashier/kitchen_staff yang tidak punya Attendance.read (lihat
// AttendanceController::kioskInfo()).
const photoRequired = ref(false)

const fetchKioskInfo = async () => {
  try {
    const response = await useApi('/api/v1/attendance/kiosk-info').get()
    const data = response?.data ?? response

    photoRequired.value = !!data?.photo_capture_enabled
  } catch {
    photoRequired.value = false
  }
}

onMounted(() => {
  fetchTenantName()
  fetchKioskInfo()
})

// ── State mesin: idle -> pin|qr -> submitting -> result -> idle ──────────
const screen = ref('idle')
const submitting = ref(false)

const pin = ref('')
const pinMasked = computed(() => '•'.repeat(pin.value.length).padEnd(6, '◦'))

const fileInput = ref(null)

const result = ref({ type: null, message: '', staffName: '', action: '', time: '', isLate: null, distance: null, radius: null, isOverride: false })

// Addendum 08, Modul AB — kredensial staff terakhir yang dicoba (diisi tiap
// submitClock() dipanggil, terlepas dari hasilnya) supaya alur override bisa
// mengirim ulang identitas staff yang SAMA tanpa memaksa staff input ulang.
const lastAttempt = ref({ method: null, value: null, latitude: null, longitude: null })

// Patch — foto verifikasi. Menampung method/value PIN/QR yang SUDAH cocok
// secara lokal (PIN selesai diketik / QR berhasil di-decode) TAPI belum
// dikirim ke server -- diisi begitu photoRequired aktif dan layar 'photo'
// dibuka, dipakai submitClock() begitu foto selesai diambil & dikompresi.
const pendingAttempt = ref({ method: null, value: null })
const verifyPhotoInput = ref(null)

const approverPin = ref('')
const approverPinMasked = computed(() => '•'.repeat(approverPin.value.length).padEnd(6, '◦'))
const overrideReason = ref('')
const overrideError = ref('')

const resetToIdle = () => {
  screen.value = 'idle'
  pin.value = ''
  approverPin.value = ''
  overrideReason.value = ''
  overrideError.value = ''
  pendingAttempt.value = { method: null, value: null }
  result.value = { type: null, message: '', staffName: '', action: '', time: '', isLate: null, distance: null, radius: null, isOverride: false }
}

let resetTimer = null

const scheduleAutoReset = (ms = 6000) => {
  clearTimeout(resetTimer)
  resetTimer = setTimeout(resetToIdle, ms)
}

onBeforeUnmount(() => clearTimeout(resetTimer))

// ── Lokasi GPS (opsional — dibiarkan kosong kalau gagal/ditolak) ─────────
const getLocationSafely = () => new Promise(resolve => {
  if (!navigator.geolocation) {
    resolve({ latitude: null, longitude: null })
    
    return
  }
  navigator.geolocation.getCurrentPosition(
    pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
    () => resolve({ latitude: null, longitude: null }),
    { timeout: 8000, enableHighAccuracy: true },
  )
})

// ── Submit ke backend ──────────────────────────────────────────────────
const showResult = payload => {
  result.value = { ...result.value, ...payload }
  screen.value = 'result'
  scheduleAutoReset()
}

const submitClock = async (method, value, photo = null) => {
  submitting.value = true
  try {
    const { latitude, longitude } = await getLocationSafely()

    // Dicatat SEBELUM request dikirim (bukan cuma saat sukses) — kalau
    // hasilnya OUTSIDE_GEOFENCE, alur override (di bawah) butuh kredensial
    // staff yang PERSIS sama tanpa staff harus input ulang PIN/scan QR lagi.
    lastAttempt.value = { method, value, latitude, longitude }

    // Patch — foto verifikasi: kalau ada, kirim sebagai multipart (FormData)
    // supaya file ikut ke body yang sama dengan method/value/lat/lng, satu
    // request saja. useApi() otomatis melepas header Content-Type kalau
    // body FormData (lihat utils/api.js), browser yang set multipart boundary.
    const response = photo
      ? await useApi('/api/v1/attendance/clock').post({ body: buildClockFormData(method, value, latitude, longitude, photo) })
      : await useApi('/api/v1/attendance/clock').post({ body: { method, value, latitude, longitude } })

    const data = response?.data ?? response

    showResult({
      type: 'success',
      staffName: data?.user?.name ?? '',
      action: data?.status === 'clocked_out' ? 'Clock-out' : 'Clock-in',
      time: data?.status === 'clocked_out' ? data?.clock_out_at : data?.clock_in_at,
      isLate: data?.is_late ?? null,
    })
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const body = err?.data ?? {}

    if (body?.code === 'OUTSIDE_GEOFENCE') {
      showResult({
        type: 'outside_geofence',
        message: body?.message ?? 'Anda berada di luar radius toko.',
        distance: body?.data?.distance_meters ?? null,
        radius: body?.data?.radius_meters ?? null,
      })
    } else if (status === 429) {
      showResult({
        type: 'rate_limited',
        message: body?.message ?? 'Terlalu banyak percobaan. Coba lagi sebentar lagi.',
      })
    } else {
      showResult({
        type: 'error',
        message: body?.message ?? 'Absen gagal. Silakan coba lagi.',
      })
    }
  } finally {
    submitting.value = false
  }
}

const buildClockFormData = (method, value, latitude, longitude, photo) => {
  const formData = new FormData()

  formData.append('method', method)
  formData.append('value', value)
  if (latitude !== null) formData.append('latitude', latitude)
  if (longitude !== null) formData.append('longitude', longitude)
  formData.append('photo', photo, 'verifikasi.jpg')

  return formData
}

// ── Foto verifikasi (opsional, toggle di Pengaturan > Absensi) ──────────
// Dipanggil setelah PIN selesai diketik / QR berhasil di-decode TAPI
// SEBELUM dikirim ke server -- kalau toggle aktif, alihkan ke layar 'photo'
// dulu (kredensial ditampung di pendingAttempt), kalau tidak langsung kirim
// seperti biasa (regresi nol untuk tenant yang tidak mengaktifkan fitur ini).
const proceedToPhotoOrSubmit = async (method, value) => {
  if (photoRequired.value) {
    pendingAttempt.value = { method, value }
    screen.value = 'photo'

    return
  }
  await submitClock(method, value)
}

const triggerVerifyCamera = () => {
  verifyPhotoInput.value?.click()
}

/**
 * Kompresi WAJIB di frontend sebelum upload (canvas resize + re-encode
 * JPEG) — target ~50-80KB per foto, cukup untuk verifikasi visual manual
 * di Laporan Kehadiran, bukan untuk kualitas cetak. Turunkan quality
 * bertahap kalau hasil pertama masih di atas target, sampai batas quality
 * minimum supaya tidak infinite loop di foto yang memang detail/besar.
 */
const compressPhoto = (file, maxBytes = 80 * 1024) => new Promise((resolve, reject) => {
  const img = new Image()
  const objectUrl = URL.createObjectURL(file)

  img.onload = async () => {
    URL.revokeObjectURL(objectUrl)

    const MAX_DIMENSION = 480
    let { width, height } = img

    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
      if (width > height) {
        height = Math.round(height * (MAX_DIMENSION / width))
        width = MAX_DIMENSION
      } else {
        width = Math.round(width * (MAX_DIMENSION / height))
        height = MAX_DIMENSION
      }
    }

    const canvas = document.createElement('canvas')

    canvas.width = width
    canvas.height = height
    canvas.getContext('2d').drawImage(img, 0, 0, width, height)

    let quality = 0.7
    let blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality))

    while (blob && blob.size > maxBytes && quality > 0.3) {
      quality -= 0.15
      blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality))
    }

    resolve(blob)
  }
  img.onerror = () => reject(new Error('Gagal memuat foto.'))
  img.src = objectUrl
})

const handleVerifyPhotoFile = async event => {
  const file = event.target.files?.[0]

  event.target.value = ''
  if (!file) return

  submitting.value = true
  try {
    const compressed = await compressPhoto(file)

    if (!compressed) {
      showResult({ type: 'error', message: 'Gagal memproses foto. Coba lagi.' })

      return
    }

    await submitClock(pendingAttempt.value.method, pendingAttempt.value.value, compressed)
  } catch (e) {
    showResult({ type: 'error', message: 'Gagal memproses foto. Coba lagi.' })
  } finally {
    submitting.value = false
  }
}

// ── Override Manager/Owner (Addendum 08, Modul AB) ────────────────────────
const openOverride = () => {
  approverPin.value = ''
  overrideReason.value = ''
  overrideError.value = ''
  screen.value = 'override'
}

const pressApproverDigit = digit => {
  if (approverPin.value.length < 6) approverPin.value += String(digit)
}

const backspaceApproverPin = () => {
  approverPin.value = approverPin.value.slice(0, -1)
}

const submitOverride = async () => {
  if (approverPin.value.length < 4 || !lastAttempt.value.method) return

  overrideError.value = ''
  submitting.value = true
  try {
    const response = await useApi('/api/v1/attendance/clock/override').post({
      body: {
        method: lastAttempt.value.method,
        value: lastAttempt.value.value,
        approver_pin: approverPin.value,
        override_reason: overrideReason.value || undefined,
        latitude: lastAttempt.value.latitude,
        longitude: lastAttempt.value.longitude,
      },
    })

    const data = response?.data ?? response

    showResult({
      type: 'success',
      staffName: data?.user?.name ?? '',
      action: data?.status === 'clocked_out' ? 'Clock-out' : 'Clock-in',
      isLate: data?.is_late ?? null,
      isOverride: true,
    })
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const body = err?.data ?? {}

    if (status === 429) {
      // Lockout PIN approver -- retry langsung tidak akan membantu, alihkan
      // ke layar hasil (sama pola dengan rate limit PIN staff).
      showResult({
        type: 'rate_limited',
        message: body?.message ?? 'Terlalu banyak percobaan. Coba lagi sebentar lagi.',
      })
    } else {
      // PIN approver salah, ATAU cocok tapi tidak punya permission
      // Attendance.override -- dua-duanya tampil sebagai pesan yang sama
      // persis dari backend (§4 addendum: approver tanpa izin tetap
      // ditolak). Tetap di layar override supaya manager bisa coba lagi.
      overrideError.value = body?.message ?? 'Override gagal. Periksa PIN approver.'
      approverPin.value = ''
    }
  } finally {
    submitting.value = false
  }
}

// ── PIN keypad ─────────────────────────────────────────────────────────
const openPin = () => {
  pin.value = ''
  screen.value = 'pin'
}

const pressDigit = digit => {
  if (pin.value.length < 6) pin.value += String(digit)
}

const backspacePin = () => {
  pin.value = pin.value.slice(0, -1)
}

const submitPin = () => {
  if (pin.value.length < 4) return
  proceedToPhotoOrSubmit('pin', pin.value)
}

// ── QR scan (foto + BarcodeDetector) ─────────────────────────────────────
const qrSupported = typeof window !== 'undefined' && 'BarcodeDetector' in window

const openQr = () => {
  if (!qrSupported) {
    showResult({ type: 'error', message: 'Kamera QR tidak didukung di perangkat ini. Gunakan Input PIN.' })
    
    return
  }
  screen.value = 'qr'
}

const triggerCamera = () => {
  fileInput.value?.click()
}

const handleQrFile = async event => {
  const file = event.target.files?.[0]

  event.target.value = '' // reset supaya foto yang sama bisa dipilih lagi
  if (!file) return

  submitting.value = true
  try {
    const bitmap = await createImageBitmap(file)
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
    const codes = await detector.detect(bitmap)

    if (!codes.length) {
      showResult({ type: 'error', message: 'Kode QR tidak terdeteksi di foto. Coba lagi.' })
      
      return
    }

    await proceedToPhotoOrSubmit('qr', codes[0].rawValue)
  } catch (e) {
    showResult({ type: 'error', message: 'Gagal membaca kode QR dari foto.' })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="kiosk-screen d-flex flex-column align-center justify-center">
    <div class="kiosk-header text-center mb-8">
      <VIcon
        icon="ri-fingerprint-line"
        size="40"
        color="primary"
        class="mb-2"
      />
      <h1 class="text-h4 font-weight-bold mb-1">
        Absensi Karyawan
      </h1>
      <p
        v-if="tenantName"
        class="text-body-1 text-medium-emphasis"
      >
        {{ tenantName }}
      </p>
    </div>

    <!-- IDLE: 2 tombol besar netral -->
    <div
      v-if="screen === 'idle'"
      class="kiosk-buttons"
    >
      <VBtn
        size="x-large"
        color="primary"
        class="kiosk-big-btn"
        prepend-icon="ri-lock-password-line"
        @click="openPin"
      >
        Input PIN
      </VBtn>
      <VBtn
        size="x-large"
        color="secondary"
        variant="tonal"
        class="kiosk-big-btn"
        prepend-icon="ri-qr-scan-2-line"
        @click="openQr"
      >
        Scan QR
      </VBtn>
    </div>

    <!-- PIN KEYPAD -->
    <VCard
      v-else-if="screen === 'pin'"
      class="kiosk-card pa-6"
      max-width="360"
    >
      <div class="text-center mb-4">
        <div class="pin-display">
          {{ pinMasked }}
        </div>
      </div>

      <div class="keypad-grid mb-4">
        <VBtn
          v-for="d in [1,2,3,4,5,6,7,8,9]"
          :key="d"
          size="x-large"
          variant="tonal"
          class="keypad-btn"
          :disabled="submitting"
          @click="pressDigit(d)"
        >
          {{ d }}
        </VBtn>
        <VBtn
          size="x-large"
          variant="text"
          class="keypad-btn"
          :disabled="submitting"
          @click="backspacePin"
        >
          <VIcon icon="ri-arrow-left-line" />
        </VBtn>
        <VBtn
          size="x-large"
          variant="tonal"
          class="keypad-btn"
          :disabled="submitting"
          @click="pressDigit(0)"
        >
          0
        </VBtn>
        <VBtn
          size="x-large"
          color="primary"
          class="keypad-btn"
          :disabled="submitting || pin.length < 4"
          :loading="submitting"
          @click="submitPin"
        >
          <VIcon icon="ri-check-line" />
        </VBtn>
      </div>

      <VBtn
        block
        variant="text"
        :disabled="submitting"
        @click="resetToIdle"
      >
        Batal
      </VBtn>
    </VCard>

    <!-- QR SCAN -->
    <VCard
      v-else-if="screen === 'qr'"
      class="kiosk-card pa-6 text-center"
      max-width="360"
    >
      <VIcon
        icon="ri-qr-scan-2-line"
        size="72"
        color="secondary"
        class="mb-4"
      />
      <p class="text-body-1 mb-6">
        Arahkan kamera ke kode QR Anda, lalu ambil foto.
      </p>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        @change="handleQrFile"
      >

      <VBtn
        block
        size="large"
        color="secondary"
        class="mb-3"
        :loading="submitting"
        :disabled="submitting"
        @click="triggerCamera"
      >
        Buka Kamera
      </VBtn>
      <VBtn
        block
        variant="text"
        :disabled="submitting"
        @click="resetToIdle"
      >
        Batal
      </VBtn>
    </VCard>

    <!-- FOTO VERIFIKASI — Patch (opsional, toggle Pengaturan > Absensi).
         Muncul SETELAH PIN diketik / QR berhasil dibaca, SEBELUM absen
         dikirim ke server -- kredensial ditampung di pendingAttempt. -->
    <VCard
      v-else-if="screen === 'photo'"
      class="kiosk-card pa-6 text-center"
      max-width="360"
    >
      <VIcon
        icon="ri-camera-line"
        size="72"
        color="primary"
        class="mb-4"
      />
      <p class="text-body-1 mb-6">
        Ambil foto verifikasi untuk melengkapi absen.
      </p>

      <input
        ref="verifyPhotoInput"
        type="file"
        accept="image/*"
        capture="user"
        hidden
        @change="handleVerifyPhotoFile"
      >

      <VBtn
        block
        size="large"
        color="primary"
        class="mb-3"
        :loading="submitting"
        :disabled="submitting"
        @click="triggerVerifyCamera"
      >
        Ambil Foto
      </VBtn>
      <VBtn
        block
        variant="text"
        :disabled="submitting"
        @click="resetToIdle"
      >
        Batal
      </VBtn>
    </VCard>

    <!-- OVERRIDE: PIN approver + alasan (Addendum 08, Modul AB) -->
    <VCard
      v-else-if="screen === 'override'"
      class="kiosk-card pa-6"
      max-width="360"
    >
      <div class="text-center mb-2">
        <VIcon icon="ri-shield-user-line" size="40" color="primary" />
        <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
          PIN Manager/Owner untuk menyetujui absen di luar radius
        </p>
      </div>

      <div class="text-center mb-4">
        <div class="pin-display">
          {{ approverPinMasked }}
        </div>
      </div>

      <div class="keypad-grid mb-4">
        <VBtn
          v-for="d in [1,2,3,4,5,6,7,8,9]"
          :key="d"
          size="x-large"
          variant="tonal"
          class="keypad-btn"
          :disabled="submitting"
          @click="pressApproverDigit(d)"
        >
          {{ d }}
        </VBtn>
        <VBtn
          size="x-large"
          variant="text"
          class="keypad-btn"
          :disabled="submitting"
          @click="backspaceApproverPin"
        >
          <VIcon icon="ri-arrow-left-line" />
        </VBtn>
        <VBtn
          size="x-large"
          variant="tonal"
          class="keypad-btn"
          :disabled="submitting"
          @click="pressApproverDigit(0)"
        >
          0
        </VBtn>
        <VBtn
          size="x-large"
          color="primary"
          class="keypad-btn"
          :disabled="submitting || approverPin.length < 4"
          :loading="submitting"
          @click="submitOverride"
        >
          <VIcon icon="ri-check-line" />
        </VBtn>
      </div>

      <VTextarea
        v-model="overrideReason"
        label="Alasan (opsional)"
        rows="2"
        density="compact"
        variant="outlined"
        class="mb-3"
        :disabled="submitting"
      />

      <VAlert v-if="overrideError" type="error" variant="tonal" density="compact" class="mb-3">
        {{ overrideError }}
      </VAlert>

      <VBtn
        block
        variant="text"
        :disabled="submitting"
        @click="resetToIdle"
      >
        Batal
      </VBtn>
    </VCard>

    <!-- RESULT -->
    <VCard
      v-else-if="screen === 'result'"
      class="kiosk-card pa-8 text-center"
      max-width="400"
    >
      <template v-if="result.type === 'success'">
        <VIcon
          icon="ri-checkbox-circle-fill"
          size="96"
          color="success"
          class="mb-4"
        />
        <h2 class="text-h5 font-weight-bold mb-1">
          {{ result.staffName }}
        </h2>
        <p class="text-body-1 mb-2">
          {{ result.action }} berhasil
        </p>
        <VChip
          v-if="result.isLate === true"
          color="warning"
          variant="tonal"
        >
          Terlambat
        </VChip>
        <VChip
          v-else-if="result.isLate === false"
          color="success"
          variant="tonal"
        >
          Tepat Waktu
        </VChip>
        <VChip
          v-if="result.isOverride"
          color="info"
          variant="tonal"
          class="mt-2"
          prepend-icon="ri-shield-user-line"
        >
          Disetujui Manager/Owner (Override)
        </VChip>
      </template>

      <template v-else-if="result.type === 'outside_geofence'">
        <VIcon
          icon="ri-map-pin-line"
          size="96"
          color="warning"
          class="mb-4"
        />
        <h2 class="text-h6 font-weight-bold mb-2">
          Di Luar Jangkauan Toko
        </h2>
        <p class="text-body-2 mb-2">
          {{ result.message }}
        </p>
        <p
          v-if="result.distance !== null"
          class="text-caption text-medium-emphasis mb-4"
        >
          Jarak Anda: {{ result.distance }} m (maksimal {{ result.radius }} m)
        </p>
        <VBtn
          block
          color="primary"
          prepend-icon="ri-shield-user-line"
          class="mb-2"
          @click="openOverride"
        >
          Minta Persetujuan Manager/Owner
        </VBtn>
      </template>

      <template v-else-if="result.type === 'rate_limited'">
        <VIcon
          icon="ri-time-line"
          size="96"
          color="error"
          class="mb-4"
        />
        <h2 class="text-h6 font-weight-bold mb-2">
          Terlalu Banyak Percobaan
        </h2>
        <p class="text-body-2">
          {{ result.message }}
        </p>
      </template>

      <template v-else>
        <VIcon
          icon="ri-close-circle-fill"
          size="96"
          color="error"
          class="mb-4"
        />
        <p class="text-body-1">
          {{ result.message }}
        </p>
      </template>

      <VBtn
        block
        variant="text"
        class="mt-6"
        @click="resetToIdle"
      >
        Kembali
      </VBtn>
    </VCard>
  </div>
</template>

<style scoped>
.kiosk-screen {
  min-block-size: 100vh;
  padding: 24px;
  background: rgb(var(--v-theme-background));
}

.kiosk-buttons {
  display: flex;
  flex-direction: column;
  gap: 20px;
  inline-size: 100%;
  max-inline-size: 360px;
}

.kiosk-big-btn {
  block-size: 88px;
  font-size: 1.25rem;
  font-weight: 600;
}

.kiosk-card {
  border-radius: 20px;
}

.pin-display {
  font-size: 2rem;
  letter-spacing: 8px;
  font-weight: 700;
}

.keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.keypad-btn {
  block-size: 72px;
  font-size: 1.5rem;
}
</style>
